import React, { useEffect, useRef, useState } from 'react';

// Turnstile TypeScript declarations
declare global {
  interface Window {
    turnstile: {
      render: (element: string | HTMLElement, options: TurnstileOptions) => string;
      reset: (widgetId?: string) => void;
      remove: (widgetId?: string) => void;
      getResponse: (widgetId?: string) => string;
      ready: (callback: () => void) => void;
    };
  }
}

interface TurnstileOptions {
  sitekey: string;
  callback?: (token: string) => void;
  'error-callback'?: () => void;
  'expired-callback'?: () => void;
  'timeout-callback'?: () => void;
  theme?: 'light' | 'dark' | 'auto';
  size?: 'normal' | 'compact';
  tabindex?: number;
  'response-field'?: boolean;
  'response-field-name'?: string;
  retry?: 'auto' | 'never';
  'retry-interval'?: number;
  'refresh-expired'?: 'auto' | 'manual' | 'never';
}

interface TurnstileProps {
  onVerify: (token: string) => void;
  onError?: () => void;
  onExpire?: () => void;
  onTimeout?: () => void;
  theme?: 'light' | 'dark' | 'auto';
  size?: 'normal' | 'compact';
  className?: string;
}

const Turnstile: React.FC<TurnstileProps> = ({
  onVerify,
  onError,
  onExpire,
  onTimeout,
  theme = 'auto',
  size = 'normal',
  className = ''
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [widgetId, setWidgetId] = useState<string | null>(null);
  const [isLoaded, setIsLoaded] = useState(false);
  const [isScriptLoaded, setIsScriptLoaded] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string>('');

    // Load Turnstile script
  useEffect(() => {
    // Check if we have the site key
    const siteKey = process.env.REACT_APP_TURNSTILE_SITE_KEY;
    if (!siteKey) {
      setHasError(true);
      setErrorMessage('Missing Turnstile configuration');
      if (onError) {
        onError();
      }
      return;
    }

    // Check if script is already loaded
    if (window.turnstile) {
      setIsScriptLoaded(true);
      setIsLoaded(true);
      return;
    }

    // Check if script is already in DOM
    const existingScript = document.querySelector('script[src*="turnstile"]');
    if (existingScript) {
      const handleLoad = () => {
        setIsScriptLoaded(true);
        setTimeout(() => setIsLoaded(true), 100);
      };
      
      existingScript.addEventListener('load', handleLoad);
      return;
    }

    const script = document.createElement('script');
    script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js';
    
    // Explicitly disable async/defer to avoid turnstile.ready() conflicts
    script.async = false;
    script.defer = false;
    
    script.onload = () => {
      setIsScriptLoaded(true);
      // Skip turnstile.ready() and proceed directly after a brief delay
      setTimeout(() => setIsLoaded(true), 100);
    };

    script.onerror = () => {
      setHasError(true);
      setErrorMessage('Failed to load security verification');
      if (onError) {
        onError();
      }
    };

    document.head.appendChild(script);

    return () => {
      // Cleanup is handled by Turnstile itself
    };
  }, [onError]);

  // Render Turnstile widget
  useEffect(() => {
    if (isLoaded && isScriptLoaded && containerRef.current && !widgetId) {
      const siteKey = process.env.REACT_APP_TURNSTILE_SITE_KEY;
      
      if (!siteKey) {
        if (onError) onError();
        return;
      }

      if (!window.turnstile) {
        if (onError) onError();
        return;
      }

      if (!window.turnstile.render) {
        if (onError) onError();
        return;
      }

      try {
        const id = window.turnstile.render(containerRef.current, {
          sitekey: siteKey,
          callback: onVerify,
          'error-callback': () => {
            if (onError) onError();
          },
          'expired-callback': () => {
            if (onExpire) onExpire();
          },
          'timeout-callback': () => {
            if (onTimeout) onTimeout();
          },
          theme,
          size,
        });

        setWidgetId(id);
      } catch (error) {
        if (onError) onError();
      }
    }
  }, [isLoaded, isScriptLoaded, onVerify, onError, onExpire, onTimeout, theme, size, widgetId]);

  // Reset function available for future use
  // const reset = () => {
  //   if (widgetId && window.turnstile) {
  //     try {
  //       window.turnstile.reset(widgetId);
  //     } catch (error) {
  //       // Handle reset error silently
  //     }
  //   }
  // };

  // Fallback for testing without Turnstile
  const handleFallbackClick = () => {
    onVerify('fallback-token-for-testing');
  };

  if (hasError) {
    return (
      <div className={`turnstile-container ${className}`}>
        <div style={{ padding: '10px', border: '1px solid #ffc107', borderRadius: '4px', backgroundColor: '#fff3cd' }}>
          <p style={{ margin: '0 0 10px 0', color: '#856404' }}>
            {errorMessage}
          </p>
          <button 
            type="button"
            onClick={handleFallbackClick}
            style={{ 
              padding: '5px 10px', 
              backgroundColor: '#ffc107', 
              border: '1px solid #ffc107',
              borderRadius: '4px',
              cursor: 'pointer'
            }}
          >
            Continue (Development Mode)
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={`turnstile-container ${className}`}>
      <div ref={containerRef} />
      {!isLoaded && !hasError && (
        <div style={{ padding: '10px', textAlign: 'center', color: '#666' }}>
          Loading security verification...
        </div>
      )}
    </div>
  );
};

export default Turnstile;
